import React, {
    ReactElement,
    useCallback,
    useMemo,
    useState,
    useEffect,
} from "react";
import { Col, Row, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useDispatch } from "react-redux";
import WidgetCard from "./WidgetCard";
import { buildWidgetCardFilters } from "@ts/revstack/ui-utils";
import {
    COLUMN_VISIBILITY_MODE,
    OptionValues,
    RevstackDashboardSubModules,
    RevstackFilterTypes,
    WidgetType,
} from "@ts/common-types";
import { WidgetConfig } from "@ts/common-types";
import { renderWidgetStructure } from "@ts/revstack/renderers";
import RevstackTabsSection from "@pages/revstack-dashboards/RevstackTabsSection";
import { updateDrilldownState, updateTrendsState } from "@features/modalSlice";
import { updateDrilldownFilters } from "@features/revstackWidgetsSlice";
import { titleCase } from "@ts/utils";
import { keys, values } from "underscore";
import { dispatchUpdateTrendsState } from "@/ts/revstack/helpers";
import { ALL_OPTIONS } from "@ts/constants";
import { drilldownClickHandler } from "@/ts/revstack/constants";
// Constants
const WIDGET_STYLES = {
    W6_COL_STYLE: {
        borderRight: "1px solid #e8e8e8",
        overflow: "hidden",
        overflowY: "auto" as const,
        padding: "14px",
        height: 500,
        margin: 0,
    },
    HEADER_PADDING: 20,
    TABLE_HEIGHT: 528,
    TABLE_HEIGHT_TABLE_ONLY: "calc(100% - 71px)",
    CARD_HEIGHT_TABLE_ONLY: 480,
} as const;

// Helper function to determine essential columns dynamically from colDefs
const getEssentialColumnKeys = (columns: ColumnsType<any> | undefined): string[] => {
    if (!columns) return [];
    
    const essentialKeys: string[] = [];
    
    columns.forEach((col: any) => {
        // Check if column is pinned (usually essential structural columns)
        if (col.fixed === "left" || col.fixed === true) {
            essentialKeys.push(col.key);
        }
        
        // Check if column has drilldown functionality (interactive columns)
        if (col.key && (
            col.key.toString().includes("drilldown") || 
            col.key.toString().includes("action") ||
            col.dataIndex && col.dataIndex.toString().includes("drilldown")
        )) {
            essentialKeys.push(col.key);
        }
    });
    
    return [...new Set(essentialKeys)]; // Remove duplicates
};

interface TableConfig {
    colDefs?: ColumnsType<any>;
    expandedColDefs?: ColumnsType<any>;
    isLayoutButtonReq?: boolean;
    isCompetitionButtonReq?: boolean;
    isDownloadButtonReq?: boolean;
    isExpandRowsButtonReq?: boolean;
    isTitleRequired?: boolean;
    isExpandable?: boolean;
    isSearchReq?: boolean;
    isGroupedTable?: boolean;
    isDrilldownTable?: boolean;
    showExpandTableHeader?: boolean;
    isCheckboxSelectionRequired?: boolean;
    style?: React.CSSProperties;
    type?: string;
    dataKeyMap?: Record<string, string>;
    defaultFilter?: string;
    filterOptions?: { label: string; value: string; sortOrder?: "asc" | "desc"; filterConfig?: { condition?: { operater: string; value: number; }; field?: string; }; }[];
    drilldown?: any;
    colVisibilityMode?: COLUMN_VISIBILITY_MODE;
}

interface IRevstackSubcategoryPerformanceWidgetProps {
    widgetConfig: {
        widgets?: WidgetConfig[][];
        config?: {
            widgetCard: any;
            hideTrendsTabSection: any;
            defaultTab: RevstackDashboardSubModules;
            module: any;
            isTrendsButtonComingSoon: any;
            trendsBtnSectionId: any;
            isTrendsButtonRequired?: boolean;
            isDrillDownButtonRequired?: boolean;
            isTableOnlyMode?: boolean;
            isTabsRequired?: boolean;
            defaultParentTabKey?: string;
            isDeepDiveButtonRequired?: boolean;
            isSelectFilterRequired?: boolean;
            isLayoutButtonReq?: boolean;
            isDownloadButtonReq?: boolean;
            isTitleRequired?: boolean;
            isSearchReq?: boolean;
            isColumnGroupSelectReq?: boolean;
            isColumnGroupSelectSameForAllTabs?: boolean;
            showCategorySelect?: boolean;
            showRegionSelect?: boolean;
            isHeaderRequired?: boolean;
            isCardRequired?: boolean;
            table: {
                colDefs?: ColumnsType<any>;
                expandedColDefs?: ColumnsType<any>;
                isLayoutButtonReq?: boolean;
                isCompetitionButtonReq?: boolean;
                isDownloadButtonReq?: boolean;
                isTitleRequired?: boolean;
                isSearchReq?: boolean;
                isExpandable?: boolean;
                colVisibilityMode?: COLUMN_VISIBILITY_MODE;
            };
        };
        sectionId: any;
    };
    widgetData: any;
    title: string;
    modalConfig: any;
    configSessionKey?: string;
    icon?: ReactElement;
    onProductSelection?: (selectedRows: any[]) => void;
    tableAdditionalFilters?: any[];
}

interface WidgetRendererProps {
    widget: WidgetConfig;
    widgetData: any;
    type: string;
    selectedCardName: string;
    setSelectedCardName: (name: string, id?: string) => void;
    widgetConfig: IRevstackSubcategoryPerformanceWidgetProps["widgetConfig"];
    configSessionKey?: string;
    isTableOnlyMode?: boolean;
    isTabsRequired?: boolean;
    parentFilterValues: Record<string, string>;
    onProductSelection?: (selectedRows: any[]) => void;
    setParentFilterValues: React.Dispatch<
        React.SetStateAction<Record<string, string>>
    >;
    parentFilters: any[];
    searchKey: string;
    searchTerms: Record<string, string>;
    searchHandler: (
        e: React.ChangeEvent<HTMLInputElement>,
        tableData: any
    ) => void;
    columnVisibilityMap: Record<string, boolean>;
    onColumnToggle: (columnKey: string) => void;
    additionalFilters?: any[];
    tableAdditionalFilters?: any[];
}

// Utility functions
const filterTableDataByParentFilters = (
    tableData: any[],
    parentFilterValues: Record<string, string>
): any[] => {
    if (!parentFilterValues || Object.keys(parentFilterValues).length === 0) {
        return tableData;
    }

    if (values(parentFilterValues).some((filterKey: any) => filterKey === OptionValues.ALL)) {
        return tableData;
    }

    return tableData.filter((item: any) => {
        return keys(parentFilterValues).every((filterKey) => {
            return item[filterKey] === parentFilterValues[filterKey];
        });
    });
};

const filterTableDataBySearch = (
    tableData: any[],
    searchTerm: string
): any[] => {
    if (!searchTerm || !tableData) return tableData;

    const searchLower = searchTerm.toLowerCase();
    return tableData.filter((row: any) => {
        return Object.values(row).some((value: any) => {
            if (typeof value === "string") {
                return value.toLowerCase().includes(searchLower);
            } else if (typeof value === "number") {
                return value.toString().toLowerCase().includes(searchLower);
            }
            return false;
        });
    });
};

const findActiveWidget = (
    widgets: WidgetConfig[][],
    activeTab: string
): WidgetConfig | null => {
    for (const widgetRow of widgets) {
        for (const widget of widgetRow) {
            if (widget.tabKey === activeTab) {
                return widget;
            }
        }
    }
    return null;
};

const processColumnsForGroups = (
    columns: any[],
    isGroupedTable: boolean,
    groups: Map<string, string>,
    colVisibilityMode: COLUMN_VISIBILITY_MODE.GROUP | COLUMN_VISIBILITY_MODE.COLUMNS = COLUMN_VISIBILITY_MODE.COLUMNS
): void => {
    if (colVisibilityMode === COLUMN_VISIBILITY_MODE.GROUP) {
        columns.forEach((col: any) => {
            if (col.children && col.children.length > 0) {
                const groupKey = col.key || col.title;
                if (groupKey) {
                    let title = col.title;
                    if (typeof title === 'object' && title !== null) {
                        if (title.props && title.props.children) {
                            title = typeof title.props.children === 'string' 
                                ? title.props.children 
                                : groupKey;
                        } else {
                            title = groupKey;
                        }
                    }
                    
                    if (!title || typeof title !== 'string') {
                        title = groupKey.toString().replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
                        title = title.charAt(0).toUpperCase() + title.slice(1);
                    }
                    
                    groups.set(groupKey.toString(), titleCase(title));
                }
            }
        });
    } else {
        // Extract individual columns for columns mode
        const extractColumns = (cols: any[]) => {
            cols.forEach((col: any) => {
                if (col.children && col.children.length > 0) {
                    extractColumns(col.children);
                } else if (col.key && col.title) {
                    let title = col.title;
                    if (typeof title === 'object' && title !== null) {
                        if (title.props && title.props.children) {
                            title = typeof title.props.children === 'string' 
                                ? title.props.children 
                                : col.key;
                        } else {
                            title = col.key;
                        }
                    }
                    
                    if (typeof title !== 'string') {
                        title = col.key.toString().replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
                        title = title.charAt(0).toUpperCase() + title.slice(1);
                    }
                    
                    groups.set(col.key.toString(), titleCase(title));
                }
            });
        };
        
        extractColumns(columns);
    }
};

const convertGroupsToOptions = (groups: Map<string, string>): Array<{ label: string; value: string }> => {
    return Array.from(groups.entries()).map(([value, label]) => ({
        label,
        value,
    }));
};

const filterColumnsByVisibilityMode = (
    columns: ColumnsType<any> | undefined,
    isGroupedTable: boolean,
    columnVisibilityMap?: Record<string, boolean>,
    colVisibilityMode: COLUMN_VISIBILITY_MODE = COLUMN_VISIBILITY_MODE.COLUMNS,
    isColumnGroupSelectSameForAllTabs = false
): ColumnsType<any> | undefined => {
    
    if (!columns) {
        return columns;
    }

    // Force group mode when isColumnGroupSelectSameForAllTabs is true
    const effectiveMode = isColumnGroupSelectSameForAllTabs ? COLUMN_VISIBILITY_MODE.GROUP : colVisibilityMode;
    
    const essentialColumnKeys = getEssentialColumnKeys(columns);
    
    // For grouped tables with nested columns, we need to handle the structure differently
    const processColumns = (cols: any[]): any[] => {
        return cols.map((col: any) => {
            // If this column has children (grouped column), process the children
            if (col.children && col.children.length > 0) {
                const groupKey = col.key || col.title;
                
                if (effectiveMode === COLUMN_VISIBILITY_MODE.GROUP) {
                    // Group mode: check if entire group should be visible
                    if (columnVisibilityMap && groupKey && columnVisibilityMap[groupKey] === false) {
                        return null; // Hide entire group
                    }
                    
                    // Group is visible, show all children
                    const filteredChildren = col.children.filter((childCol: any) => {
                        const colKey = childCol.key as string;
                        
                        // Always show essential columns (pinned + drilldown)
                        if (essentialColumnKeys.includes(colKey)) {
                            return true;
                        }
                        
                        // Group is visible, show all non-essential children
                        return true;
                    });
                    
                    return filteredChildren.length > 0 ? {
                        ...col,
                        children: filteredChildren
                    } : null;
                    
                } else if (effectiveMode === COLUMN_VISIBILITY_MODE.COLUMNS) {
                    // Columns mode: check individual column visibility
                    const filteredChildren = col.children.filter((childCol: any) => {
                        const colKey = childCol.key as string;
                        
                        // Always show essential columns (pinned + drilldown)
                        if (essentialColumnKeys.includes(colKey)) {
                            return true;
                        }
                        
                        // Check individual column visibility
                        return columnVisibilityMap ? columnVisibilityMap[colKey] !== false : true;
                    });
                    
                    // Return the group column with filtered children
                    // Only include the group if it has visible children
                    return filteredChildren.length > 0 ? {
                        ...col,
                        children: filteredChildren
                    } : null;
                    
                } else {
                    // No filtering specified, show all children
                    return col;
                }
            } else {
                // This is a regular column (not grouped)
                const colKey = col.key as string;
                const colDataIndex = col.dataIndex as string;
                
                // Always show essential columns (pinned + drilldown)
                if (essentialColumnKeys.includes(colKey) || 
                    essentialColumnKeys.includes(colDataIndex)) {
                    return col;
                }
                
                // Check individual column visibility
                if (columnVisibilityMap) {
                    return columnVisibilityMap[colKey] !== false ? col : null;
                }
                
                return col;
            }
        }).filter(Boolean);
    };

    const result = processColumns(columns);
    return result;
};

const createFilterConfigurations = (
    parentFilters: any[],
    getAvailableColumns: any[],
    widgetConfig: any,
    selectedCardName: string,
    disableDropdown: boolean,
    dispatch: any,
    modalConfig: any,
    columnVisibilityMap: Record<string, boolean>,
    onColumnToggle: (columnKey: string) => void,
    widgetData: any,
    selectedCardId: string
) => {
    const filters: any[] = [...parentFilters];

    // Column group select filter - now using columnVisibilityMap
    if (widgetConfig?.config?.isColumnGroupSelectReq && getAvailableColumns.length > 1) {
        const selectedOptions = getAvailableColumns
            .filter(option => columnVisibilityMap[option.value] !== false)
            .map(option => option.value);

        const handleColumnSelection = (value: string[]) => {
            const currentSelected = getAvailableColumns
                .filter(option => columnVisibilityMap[option.value] !== false)
                .map(option => option.value);
                
            const toAdd = value.filter(v => !currentSelected.includes(v));
            const toRemove = currentSelected.filter(v => !value.includes(v));
            
            [...toAdd, ...toRemove].forEach(optionValue => onColumnToggle(optionValue));
        };

        filters.push({
            section: "" as const,
            type: RevstackFilterTypes.MULTI_SELECT,
            props: {
                options: getAvailableColumns,
                value: selectedOptions,
                onChange: handleColumnSelection,
                width: 180,
                placeholder: "Select Columns",
                showAllOption: false,
                maxDisplayItems: 1,
                useTags: true,
            },
        });
    }

    // Trends button filter
    if (widgetConfig?.config?.isTrendsButtonRequired) {
        filters.push({
            section: "" as const,
            type: RevstackFilterTypes.TRENDS,
            props: {
                onClick: () => {
                    dispatchUpdateTrendsState(dispatch, widgetConfig, [])
                },
            },
            isComingSoon: widgetConfig?.config?.isTrendsButtonComingSoon,
        });
    }

    // Deep dive button filter
    if (widgetConfig?.config?.isDeepDiveButtonRequired) {
        filters.push({
            section: "" as const,
            type: RevstackFilterTypes.DEEP_DIVE,
            props: {
                onClick: () => {
                    // TODO: Implement deep dive functionality
                },
            },
            isComingSoon: true,
        });
    }

    // Drill down button filter
    if (widgetConfig?.config?.isDrillDownButtonRequired) {

        filters.push({
            section: "" as const,
            type: RevstackFilterTypes.DRILL_DOWN,
            props: {
                onClick: async () => {
                    // Update drilldown filters before opening the drawer
                    const w6Widget = widgetConfig?.widgets?.[0]?.find((w: any) => w.type === WidgetType.W6);
                    if (w6Widget) {
                        const { cardsData, topCardData } = w6Widget.formatter(w6Widget, widgetData, w6Widget.config?.card?.cardType);
                        const allCardsData = [topCardData, ...cardsData];
                        const cardType = w6Widget.config?.card?.cardType;
                        
                        if (allCardsData.length > 0) {
                            // Use the selectedCardId from state, or default to top card
                            const currentCardId = selectedCardId || topCardData?.id || allCardsData[0]?.id;
                            
                            // Determine the selected value - keep "all" as string, convert others to number
                            const selectedValue = currentCardId === ALL_OPTIONS.value 
                                ? [ALL_OPTIONS.value] 
                                : [+currentCardId];
                            
                            dispatch(updateDrilldownFilters({
                                options: {
                                    [cardType]: allCardsData.map((item: any) => ({
                                        label: titleCase(item.name),
                                        value: item?.id
                                            ? item?.id
                                            : +item?.[`${w6Widget?.config?.card?.cardId}`],
                                    })),
                                },
                                selected: {
                                    [cardType]: selectedValue,
                                },
                            }));
                        }
                    }
                    const primaryFilters = widgetConfig?.config?.header?.components?.find((component: any) => component.section === "main")?.elements[0] || {};
                    const secondaryFilters = widgetConfig?.config?.header?.components?.find((component: any) => component.section === "filters")?.elements[0] || {};
                    const isPrimaryFilterDefault = primaryFilters?.defaultTitle;

                    await drilldownClickHandler({
                        primaryFilters,
                        secondaryFilters,
                        record: {},
                        ...(isPrimaryFilterDefault ? {} : { title: selectedCardName }),
                        module: widgetConfig?.config?.module,
                        activeSectionId: widgetConfig?.sectionId,
                    });   
                },
                disabled: disableDropdown,
            },
            isComingSoon: disableDropdown,
        });
    }

    return filters;
};

const TableHeaderSection: React.FC<{
    title: string;
    titleIcon: React.ReactNode;
}> = React.memo(({ title, titleIcon }) => (
    <div className="w-100">
        <Space align="center" direction="horizontal" size={12}>
            <div className="icon-btn">{titleIcon}</div>
            <Space align="center" direction="horizontal" size={12}>
                <h4 className="mb-0">{title || "Performance Table"}</h4>
            </Space>
        </Space>
    </div>
));

const HeaderSection: React.FC<{
    title: string;
    icon: ReactElement;
    filters: any[];
}> = ({ title, icon, filters }) => (
    <div
        className="d-flex align-items-center justify-content-between border-bottom"
        style={{ padding: WIDGET_STYLES.HEADER_PADDING }}
    >
        <TableHeaderSection title={title} titleIcon={icon} />
        {buildWidgetCardFilters({ filters })}
    </div>
);

const WidgetRenderer: React.FC<WidgetRendererProps> = React.memo(
    ({
        widgetConfig,
        widget,
        widgetData,
        type,
        selectedCardName,
        setSelectedCardName,
        configSessionKey,
        isTableOnlyMode,
        isTabsRequired,
        parentFilterValues,
        setParentFilterValues,
        parentFilters,
        searchKey,
        searchTerms,
        searchHandler,
        columnVisibilityMap,
        onColumnToggle,
        onProductSelection,
        tableAdditionalFilters,
    }: WidgetRendererProps) => {
        const renderW6Widget = useCallback(() => {
            const { cardsData, topCardData, isLoading } = widget.formatter(
                widget,
                widgetData,
                type
            );
            const relatedComponentDataKeys = widget?.config?.relatedComponentDataKeys;
            const WidgetComponent = widget.widgetComponent;

            return (
                <Col span={6} style={WIDGET_STYLES.W6_COL_STYLE}>
                    <WidgetComponent
                        widgetConfig={widget?.config}
                        cardData={cardsData}
                        type={type}
                        isTopCardRequired={widget.isTopCardRequired}
                        setSelectedCardName={setSelectedCardName}
                        topCardData={topCardData}
                        isLoading={isLoading}
                        relatedComponentDataKeys={relatedComponentDataKeys}
                        configSessionKey={configSessionKey}
                    />
                </Col>
            );
        }, [
            widget,
            widgetData,
            type,
            setSelectedCardName,
            configSessionKey,
            isTabsRequired,
        ]);

        const renderW7Widget = useCallback(() => {
            const formatterOutput = widget.formatter(widgetConfig, widget, widgetData, type);
            const { tableData, expandedTableData = [], isLoading, childFilters } = formatterOutput;

            // Apply filters to table data
            let filteredTableData = filterTableDataByParentFilters(tableData, parentFilterValues);
            filteredTableData = filterTableDataBySearch(filteredTableData, searchTerms[searchKey]);

            const WidgetComponent = widget.widgetComponent;
            const tableConfig = widget?.config?.table as TableConfig;
            const isGroupedTable = tableConfig?.isGroupedTable;

            // Filter columns based on selected groups
            const columns = tableConfig?.colDefs;
            const expandedColDefs = tableConfig?.expandedColDefs;
            const filteredColumns = filterColumnsByVisibilityMode(
                columns,
                isGroupedTable,
                columnVisibilityMap,
                tableConfig?.colVisibilityMode || COLUMN_VISIBILITY_MODE.COLUMNS,
                widgetConfig?.config?.isColumnGroupSelectSameForAllTabs || false
            );
            const filteredExpandedColumns = filterColumnsByVisibilityMode(
                expandedColDefs,
                isGroupedTable,
                columnVisibilityMap,
                tableConfig?.colVisibilityMode || COLUMN_VISIBILITY_MODE.COLUMNS,
                widgetConfig?.config?.isColumnGroupSelectSameForAllTabs || false
            );
            const expandedColumns = isTableOnlyMode ? filteredExpandedColumns : expandedColDefs;
            const modifiedColumns = isTableOnlyMode
                ? filteredColumns?.filter((col) => col?.key !== "drilldown")
                : filteredColumns;

            return (
                <Col
                    span={isTableOnlyMode ? 24 : 18}
                    style={isTableOnlyMode ? { height: "100%" } : {}}
                >
                    <WidgetComponent
                        parentConfig={widgetConfig}
                        config={widget}
                        colDefs={modifiedColumns}
                        originalColDefs={columns}
                        expandedColumns={expandedColumns}
                        type={type}
                        selectedCardName={selectedCardName}
                        tableData={filteredTableData}
                        expandedTableData={expandedTableData}
                        isLoading={isLoading}
                        isExpandable={tableConfig?.isExpandable || false}
                        childFilters={childFilters}
                        noPadding={isTableOnlyMode}
                        isTableOnlyMode={isTableOnlyMode}
                        isGroupedTable={tableConfig?.isGroupedTable}
                        isDrilldownTable={tableConfig?.isDrilldownTable}
                        isLayoutButtonReq={tableConfig?.isLayoutButtonReq}
                        isCompetitionButtonReq={tableConfig?.isCompetitionButtonReq}
                        isDownloadButtonReq={tableConfig?.isDownloadButtonReq}
                        isTitleRequired={tableConfig?.isTitleRequired}
                        isSearchReq={tableConfig?.isSearchReq}
                        isExpandRowsButtonReq={tableConfig?.isExpandRowsButtonReq}
                        expandRowsButtonHandler={() => {}}
                        searchHandler={searchHandler}
                        showExpandTableHeader={tableConfig?.showExpandTableHeader}
                        style={tableConfig?.style}
                        searchValue={searchTerms[searchKey] || ""}
                        columnVisibilityMap={columnVisibilityMap}
                        onColumnToggle={onColumnToggle}
                        colVisibilityMode={tableConfig?.colVisibilityMode}
                        isCheckboxSelectionRequired={tableConfig?.isCheckboxSelectionRequired}
                        onProductSelection={onProductSelection}
                        additionalFilters={tableAdditionalFilters}
                    />
                </Col>
            );
        }, [
            widget,
            widgetData,
            type,
            selectedCardName,
            isTableOnlyMode,
            parentFilterValues,
            searchTerms,
            searchKey,
            searchHandler,
            columnVisibilityMap,
            widgetConfig,
            onColumnToggle,
            onProductSelection,
            tableAdditionalFilters,
        ]);

        if (widget.type === WidgetType.W6) {
            return renderW6Widget();
        }

        if (widget.type === WidgetType.W7) {
            return renderW7Widget();
        }

        return null;
    }
);

const RevstackTablePerformanceWidget: React.FC<
    IRevstackSubcategoryPerformanceWidgetProps
> = ({
    widgetConfig,
    modalConfig,
    widgetData,
    title,
    configSessionKey,
    icon,
    onProductSelection,
    tableAdditionalFilters,
}) => {
    const [selectedCardName, setSelectedCardName] = useState<string>("");
    const [selectedCardId, setSelectedCardId] = useState<string>("");
    
    const [parentFilterValues, setParentFilterValues] = useState<
        Record<string, string>
    >({});
    const [activeTab, setActiveTab] = useState<string>("");
    const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
    const [columnVisibilityMap, setColumnVisibilityMap] = useState<Record<string, boolean>>({});
    const dispatch = useDispatch();
    
    const isHeaderRequired = widgetConfig?.config?.isHeaderRequired ?? true;
    const isTableOnlyMode = widgetConfig?.config?.isTableOnlyMode ?? false;
    const isTabsRequired = widgetConfig?.config?.isTabsRequired ?? false;
    const isCardRequired = widgetConfig?.config?.widgetCard?.isCardRequired ?? true;

    // Wrapper function to handle both name and ID when a card is selected
    const handleSetSelectedCardName = useCallback((name: string, id?: string) => {
        setSelectedCardName(name);
        if (id) {
            setSelectedCardId(id);
        }
    }, []);

    // Search handler function that maintains separate search terms per tab/widget
    const createSearchHandler = useCallback(
        (tabKey?: string, widgetKey?: string) => {
            return (e: React.ChangeEvent<HTMLInputElement>, tableData: any) => {
                const value = e.target.value;
                const searchKey = tabKey || widgetKey || "default";
                setSearchTerms((prev) => ({
                    ...prev,
                    [searchKey]: value,
                }));
            };
        },
        []
    );

    useEffect(() => {
        if (isTabsRequired && widgetConfig?.widgets?.length > 0) {
            const firstWidget = widgetConfig.widgets[0]?.[0];
            if (firstWidget?.tabKey && !activeTab) {
                setActiveTab(widgetConfig?.config?.defaultParentTabKey || firstWidget.tabKey);
            }
        }
    }, [isTabsRequired, widgetConfig?.widgets, activeTab]);

    // Reset column group selection when tab changes (only if not same for all tabs)

    const disableDropdown = useMemo(() => {
        return (
            !selectedCardName ||
            isTableOnlyMode
        );
    }, [selectedCardName, widgetConfig.sectionId, isTableOnlyMode]);

    const parentFilters = useMemo(() => {
        const filters: any[] = [];

        if (!widgetConfig?.widgets) return filters;

        widgetConfig.widgets.forEach((widgetRow: any[]) => {
            widgetRow.forEach((widget: any) => {
                if (widget.type !== WidgetType.W7) return;

                const formatterOutput = widget.formatter(
                    widgetConfig,
                    widget,
                    widgetData,
                    widget.config?.table?.type
                );

                if (!formatterOutput.parentFilters?.length) return;

                formatterOutput.parentFilters.forEach((filter: any) => {
                    const currentValue = parentFilterValues[filter.dataKey] || filter.defaultFilter;
                    const validCurrentValue = filter.filterOptions?.find(
                        (opt: any) => opt.value === currentValue
                    )?.value || filter.filterOptions?.[0]?.value;

                    filters.push({
                        section: "" as const,
                        type: filter.type,
                        props: {
                            options: filter.filterOptions || [],
                            onChange: (value: string) => {
                                setParentFilterValues(prev => ({
                                    ...prev,
                                    [filter.dataKey]: value,
                                }));
                            },
                            value: validCurrentValue,
                            width: 180,
                        },
                    });
                });
            });
        });

        return filters;
    }, [widgetConfig, widgetData, parentFilterValues]);



    const getAvailableColumns = useMemo(() => {
        if (!widgetConfig?.widgets) return [];

        const isColumnGroupSelectSameForAllTabs =
            widgetConfig?.config?.isColumnGroupSelectSameForAllTabs;

        if (isColumnGroupSelectSameForAllTabs) {
            // For same across all tabs, get columns from all W7 widgets and merge them
            const allGroups = new Map<string, string>();

            widgetConfig.widgets.forEach((widgetRow: any[]) => {
                widgetRow.forEach((widget: any) => {
                    if (widget.type === WidgetType.W7) {
                        const tableConfig = widget?.config?.table as TableConfig;
                        const columns = tableConfig?.colDefs;

                        if (columns) {
                            processColumnsForGroups(
                                columns,
                                tableConfig?.isGroupedTable,
                                allGroups,
                                COLUMN_VISIBILITY_MODE.GROUP
                            );
                        }
                    }
                });
            });

            return convertGroupsToOptions(allGroups);
        } else {
            // For different per tab, get columns from active widget only
            // If tabs are required, use active tab logic
            if (widgetConfig?.config?.isTabsRequired) {
                if (!activeTab) return [];

                const activeWidget = findActiveWidget(widgetConfig.widgets, activeTab);
                if (!activeWidget || activeWidget.type !== WidgetType.W7) return [];

                const tableConfig = activeWidget?.config?.table as TableConfig;
                const columns = tableConfig?.colDefs;

                if (!columns) return [];

                const groups = new Map<string, string>();
                processColumnsForGroups(columns, tableConfig?.isGroupedTable, groups, tableConfig?.colVisibilityMode || COLUMN_VISIBILITY_MODE.COLUMNS);

                return convertGroupsToOptions(groups);
            } else {
                // No tabs - get columns from the first available W7 widget
                const firstWidget = widgetConfig.widgets[0]?.[0];
                if (!firstWidget || firstWidget.type !== WidgetType.W7) return [];

                const tableConfig = firstWidget?.config?.table as TableConfig;
                const columns = tableConfig?.colDefs;

                if (!columns) return [];

                // For individual column visibility, we need actual columns, not groups
                // Extract all columns including nested ones from grouped tables
                const allColumns: any[] = [];
                const extractColumns = (cols: any[]) => {
                    cols.forEach((col: any) => {
                        if (col.children && col.children.length > 0) {
                            // This is a group with children, extract the children
                            extractColumns(col.children);
                        } else if (col.key) {
                            // This is an actual data column
                            allColumns.push(col);
                        }
                    });
                };
                
                extractColumns(columns);

                // Convert individual columns to options (not groups)
                const options = allColumns.map(col => ({
                    label: col.title || col.key,
                    value: col.key
                }));
                return options;
            }
        }
    }, [
        widgetConfig?.widgets,
        activeTab,
        widgetConfig?.config?.isColumnGroupSelectSameForAllTabs,
    ]);


    // Initialize column visibility map when available columns change
    useEffect(() => {
        if (getAvailableColumns.length > 0) {
            const initialVisibilityMap: Record<string, boolean> = {};
            getAvailableColumns.forEach((option) => {
                initialVisibilityMap[option.value] = true; // All columns/groups visible by default
            });
            setColumnVisibilityMap(initialVisibilityMap);
        }
    }, [getAvailableColumns]);

    // Also initialize from the actual widget columns as a fallback
    useEffect(() => {
        if (!widgetConfig?.widgets || columnVisibilityMap && Object.keys(columnVisibilityMap).length > 0) return;
        
        // Get columns from the active widget
        const activeWidget = isTabsRequired ? findActiveWidget(widgetConfig.widgets, activeTab) : widgetConfig.widgets[0]?.[0];
        
        if (activeWidget?.type === WidgetType.W7) {
            const tableConfig = activeWidget?.config?.table as TableConfig;
            const columns = tableConfig?.colDefs;
            const colVisibilityMode = tableConfig?.colVisibilityMode || COLUMN_VISIBILITY_MODE.COLUMNS;
            
            if (columns && columns.length > 0) {
                const initialVisibilityMap: Record<string, boolean> = {};
                
                if (colVisibilityMode === COLUMN_VISIBILITY_MODE.GROUP) {
                    columns.forEach((col: any) => {
                        if (col.children && col.children.length > 0) {
                            const groupKey = col.key || col.title;
                            if (groupKey) {
                                initialVisibilityMap[groupKey] = true;
                            }
                        }
                    });
                } else {
                    // For column mode, extract all individual columns from nested structure
                    const extractColumns = (cols: any[]) => {
                        cols.forEach((col: any) => {
                            if (col.children && col.children.length > 0) {
                                extractColumns(col.children);
                            } else if (col.key) {
                                initialVisibilityMap[col.key] = true;
                            }
                        });
                    };
                    
                    extractColumns(columns);
                }
                
                setColumnVisibilityMap(initialVisibilityMap);
            }
        }
    }, [widgetConfig?.widgets, activeTab, isTabsRequired, columnVisibilityMap]);

    const handleColumnToggle = useCallback((columnKey: string) => {
        setColumnVisibilityMap(prev => {
            const newMap = {
                ...prev,
                [columnKey]: !prev[columnKey]
            };
            return newMap;
        });
        
        
    }, [columnVisibilityMap]);

    // Create tabs items for RevstackTabsSection
    const tabsItems = useMemo(() => {
        if (!widgetConfig?.config?.isTabsRequired || !widgetConfig?.widgets)
            return [];

        const items: any[] = [];

        widgetConfig.widgets.forEach((widgetRow: any[]) => {
            widgetRow.forEach((widget: any) => {
                if (widget.tabKey) {
                    items.push({
                        key: widget.tabKey,
                        label: (
                            <span className="d-flex align-items-center gap-2">
                                {widget.icon}
                                {widget.title}
                            </span>
                        ),
                        children: null, // Will be set dynamically based on activeTab
                    });
                }
            });
        });

        return items;
    }, [widgetConfig?.widgets, widgetConfig?.config?.isTabsRequired]);

    // Render widgets for the active tab
    const renderActiveTabContent = useMemo(() => {
        if (!widgetConfig?.widgets || !activeTab) return null;

        const activeWidget = findActiveWidget(widgetConfig.widgets, activeTab);
        if (!activeWidget) return null;

        // Filter widgets based on table-only mode
        if (isTableOnlyMode && activeWidget.type !== WidgetType.W7) {
            return null;
        }

        return (
            <WidgetRenderer
                key={`${activeWidget.type}-${activeTab}`}
                widget={activeWidget}
                widgetData={widgetData}
                type={activeWidget.config?.table?.type}
                selectedCardName={selectedCardName}
                setSelectedCardName={handleSetSelectedCardName}
                widgetConfig={widgetConfig}
                configSessionKey={configSessionKey}
                isTableOnlyMode={isTableOnlyMode}
                parentFilterValues={parentFilterValues}
                setParentFilterValues={setParentFilterValues}
                parentFilters={parentFilters}
                searchKey={activeTab}
                searchTerms={searchTerms}
                searchHandler={createSearchHandler(activeTab)}
                columnVisibilityMap={columnVisibilityMap}
                onColumnToggle={handleColumnToggle}
            />
        );
    }, [
        widgetConfig?.widgets,
        widgetData,
        selectedCardName,
        configSessionKey,
        isTableOnlyMode,
        parentFilterValues,
        setParentFilterValues,
        parentFilters,
        activeTab,
        searchTerms,
        createSearchHandler,
        columnVisibilityMap,
        handleColumnToggle,
        handleSetSelectedCardName,
    ]);

    const renderedWidgets = useMemo(() => {
        if (!widgetConfig?.widgets) return null;

        return renderWidgetStructure(
            widgetConfig.widgets,
            (widget: any, widgetIndex: any, keyPrefix = "") => {
                // Filter widgets based on table-only mode
                if (isTableOnlyMode && widget.type !== WidgetType.W7) {
                    return null;
                }

                const widgetKey = `${widget.type}-${keyPrefix}-${widgetIndex}`;
                return (
                    <WidgetRenderer
                        key={widgetKey}
                        widget={widget}
                        widgetData={widgetData}
                        type={widget.config?.table?.type}
                        selectedCardName={selectedCardName}
                        setSelectedCardName={handleSetSelectedCardName}
                        widgetConfig={widgetConfig}
                        configSessionKey={configSessionKey}
                        isTableOnlyMode={isTableOnlyMode}
                        parentFilterValues={parentFilterValues}
                        setParentFilterValues={setParentFilterValues}
                        parentFilters={parentFilters}
                        searchKey={widgetKey}
                        searchTerms={searchTerms}
                        searchHandler={createSearchHandler(
                            undefined,
                            widgetKey
                        )}
                        columnVisibilityMap={columnVisibilityMap}
                        onColumnToggle={handleColumnToggle}
                        onProductSelection={onProductSelection}
                        tableAdditionalFilters={tableAdditionalFilters}
                    />
                );
            }
        );
    }, [
        widgetConfig?.widgets,
        widgetData,
        selectedCardName,
        configSessionKey,
        isTableOnlyMode,
        parentFilterValues,
        setParentFilterValues,
        searchTerms,
        createSearchHandler,
        columnVisibilityMap,
        handleColumnToggle,
        handleSetSelectedCardName,
        onProductSelection,
        tableAdditionalFilters,
    ]);

    const renderContent = useCallback((content: React.ReactNode, hasTabs = false) => {
        if (!isCardRequired) {
            return content;
        }

        const cardClassName = "table-card w-100 my-3";
        const bodyPaddingClass = isTableOnlyMode ? (hasTabs ? "" : "p-0") : "";
        const cardStyle = isTableOnlyMode
            ? hasTabs
                ? {}
                : {
                      height: WIDGET_STYLES.CARD_HEIGHT_TABLE_ONLY,
                      display: "flex" as const,
                      flexDirection: "column" as const,
                  }
            : {};

        return (
            <WidgetCard
                className={cardClassName}
                bodyPaddingClass={bodyPaddingClass}
                style={cardStyle}
            >
                {content}
            </WidgetCard>
        );
    }, [isCardRequired, isTableOnlyMode]);

    // Render tabs if isTabsRequired is true
    if (widgetConfig?.config?.isTabsRequired) {

        const filters = createFilterConfigurations(
            parentFilters,
            getAvailableColumns,
            widgetConfig,
            selectedCardName,
            disableDropdown,
            dispatch,
            modalConfig,
            columnVisibilityMap,
            handleColumnToggle,
            widgetData,
            selectedCardId
        );

        const content = (
            <>
                {isHeaderRequired && (
                    <HeaderSection
                        title={title || "Sub Category Performance"}
                        icon={icon}
                        filters={filters}
                    />
                )}

                <RevstackTabsSection
                    items={tabsItems.map((item) => ({
                        ...item,
                        children:
                            item.key === activeTab ? (
                                <Row
                                    className="w-100"
                                    style={{ height: "100%" }}
                                >
                                    {renderActiveTabContent}
                                </Row>
                            ) : null,
                    }))}
                    activeTab={activeTab as RevstackDashboardSubModules}
                    setActiveTab={setActiveTab}
                    type="default-table"
                />
            </>
        );

        return renderContent(content, true);
    }

    // Render without tabs (original behavior)
    const filters = createFilterConfigurations(
        parentFilters,
        getAvailableColumns,
        widgetConfig,
        selectedCardName,
        disableDropdown,
        dispatch,
        modalConfig,
        columnVisibilityMap,
        handleColumnToggle,
        widgetData,
        selectedCardId
    );

    const content = (
        <>
            {isHeaderRequired && (
                <HeaderSection
                    title={title || "Sub Category Performance"}
                    icon={icon}
                    filters={filters}
                />
            )}

            <Row
                className="w-100"
                style={{
                    height: isTableOnlyMode ? WIDGET_STYLES.TABLE_HEIGHT_TABLE_ONLY : WIDGET_STYLES.TABLE_HEIGHT,
                }}
            >
                {renderedWidgets}
            </Row>
        </>
    );

    return renderContent(content, false);
};

export default RevstackTablePerformanceWidget;